import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ViewListIcon from "@material-ui/icons/ViewList";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import { FormatListBulletedSharp } from "@material-ui/icons";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import ptBR from "date-fns/locale/pt-BR";
import "react-big-calendar/lib/css/react-big-calendar.css";

const getUrlParam = (param) =>
  new URLSearchParams(window.location.search).get(param);

const locales = { "pt-BR": ptBR };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const calendarMessages = {
  next: "Próximo",
  previous: "Anterior",
  today: "Hoje",
  month: "Mês",
  week: "Semana",
  day: "Dia",
  agenda: "Agenda",
  date: "Data",
  time: "Hora",
  event: "Mensagem",
  noEventsInRange: "Nenhum agendamento neste período.",
  showMore: (total) => `+${total} mais`,
  allDay: "Dia todo",
};

const STATUS_CONFIG = {
  pendente: { label: "Pendente", color: "#e65100", bg: "#fff3e0", calColor: "#ff9800" },
  enviado:  { label: "Enviado",  color: "#2e7d32", bg: "#e8f5e9", calColor: "#4caf50" },
  erro:     { label: "Erro",     color: "#c62828", bg: "#ffebee", calColor: "#f44336" },
};

const getStatusKey = (status = "") => {
  const s = status.toLowerCase();
  if (s.includes("env")) return "enviado";
  if (s.includes("erro") || s.includes("fail")) return "erro";
  return "pendente";
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];
    schedules.forEach((schedule) => {
      const idx = state.findIndex((s) => s.id === schedule.id);
      if (idx !== -1) state[idx] = schedule;
      else newSchedules.push(schedule);
    });
    return [...state, ...newSchedules];
  }
  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const idx = state.findIndex((s) => s.id === schedule.id);
    if (idx !== -1) { state[idx] = schedule; return [...state]; }
    return [schedule, ...state];
  }
  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;
    const idx = state.findIndex((s) => s.id === scheduleId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  calendarPaper: {
    flex: 1,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  calendarWrapper: {
    flex: 1,
    minHeight: 580,
    "& .rbc-toolbar": {
      flexWrap: "wrap",
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
    },
    "& .rbc-toolbar button": {
      borderRadius: 6,
      padding: "4px 10px",
      fontSize: "0.8rem",
      cursor: "pointer",
      color: theme.palette.text.primary,
      background: "transparent",
      borderColor: theme.palette.divider,
    },
    "& .rbc-toolbar button:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    "& .rbc-toolbar button:focus": {
      color: theme.palette.text.primary,
    },
    "& .rbc-toolbar button.rbc-active": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      borderColor: theme.palette.primary.main,
    },
    "& .rbc-toolbar button.rbc-active:hover": {
      backgroundColor: theme.palette.primary.dark,
      color: "#fff",
    },
    "& .rbc-toolbar-label": {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    "& .rbc-event": {
      borderRadius: 4,
      padding: "1px 5px",
    },
    "& .rbc-show-more": {
      color: theme.palette.primary.main,
      fontWeight: 600,
      fontSize: "0.75rem",
    },
    "& .rbc-off-range-bg": {
      backgroundColor: theme.palette.type === "dark" ? "#1a1a1a" : "#f5f5f5",
    },
    "& .rbc-today": {
      backgroundColor: theme.palette.type === "dark" ? "#1a2744" : "#e8f4fd",
    },
    "& .rbc-header": {
      borderBottomColor: theme.palette.divider,
      color: theme.palette.text.primary,
      padding: "6px 4px",
      fontWeight: 600,
    },
    "& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view": {
      borderColor: theme.palette.divider,
      borderRadius: 8,
      overflow: "hidden",
    },
    "& .rbc-day-bg + .rbc-day-bg": { borderColor: theme.palette.divider },
    "& .rbc-month-row + .rbc-month-row": { borderColor: theme.palette.divider },
    "& .rbc-header + .rbc-header": { borderColor: theme.palette.divider },
    "& .rbc-time-content, & .rbc-time-header-content": { borderColor: theme.palette.divider },
    "& .rbc-label, & .rbc-time-slot": { color: theme.palette.text.secondary, fontSize: "0.75rem" },
    "& .rbc-date-cell": { color: theme.palette.text.primary },
    "& .rbc-agenda-date-cell, & .rbc-agenda-time-cell, & .rbc-agenda-event-cell": {
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
    },
    "& .rbc-agenda-table": { borderColor: theme.palette.divider },
    "& .rbc-agenda-table thead tr th": {
      borderBottomColor: theme.palette.divider,
      color: theme.palette.text.secondary,
    },
  },
  legendRow: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(1.5),
    alignItems: "center",
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    padding: theme.spacing(1, 1, 0.5),
    alignItems: "center",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
}));

const Schedules = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));
  const [viewMode, setViewMode] = useState("calendar");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [selectedDate, setSelectedDate] = useState(null);
  const [showTasks, setShowTasks] = useState(false);
  const [calTasks, setCalTasks] = useState([]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) handleOpenScheduleModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => fetchSchedules(), 500);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber, contactId, fetchSchedules, handleOpenScheduleModalFromContactId]);

  useEffect(() => {
    handleOpenScheduleModalFromContactId();
    const socket = socketConnection({ companyId: user.companyId });

    socket.on(`company-${user.companyId}-schedule`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.id });
      }
    });

    return () => socket.disconnect();
  }, [handleOpenScheduleModalFromContactId, user]);

  useEffect(() => {
    if (!showTasks) { setCalTasks([]); return; }
    api.get("/tasks").then(({ data }) => setCalTasks(data)).catch(() => {});
  }, [showTasks]);

  const cleanContact = () => setContactId("");

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setSelectedDate(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setSelectedDate(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => setSearchParam(event.target.value.toLowerCase());

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setSelectedDate(null);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  // Calendar
  const calendarEvents = useMemo(() => {
    const scheduleEvts = schedules.map((s) => ({
      id: s.id,
      title: s.contact?.name || "Sem contato",
      start: new Date(s.sendAt),
      end: new Date(s.sendAt),
      resource: s,
    }));
    if (!showTasks) return scheduleEvts;
    const taskEvts = calTasks
      .filter((t) => t.dueDate)
      .map((t) => ({
        id: `task-${t.id}`,
        title: (t.completed ? "✓ " : "● ") + t.text,
        start: new Date(t.dueDate),
        end: new Date(t.dueDate),
        allDay: true,
        resource: { ...t, isTask: true },
      }));
    return [...scheduleEvts, ...taskEvts];
  }, [schedules, showTasks, calTasks]);

  const eventStyleGetter = useCallback((event) => {
    if (event.resource?.isTask) {
      const PRIORITY_COLORS = { high: "#7c3aed", medium: "#6d28d9", low: "#5b21b6" };
      const bg = PRIORITY_COLORS[event.resource.priority] || "#7c3aed";
      return {
        style: {
          backgroundColor: bg,
          border: "2px dashed rgba(255,255,255,0.4)",
          borderRadius: 4,
          color: "#fff",
          fontSize: "0.72rem",
          opacity: event.resource.completed ? 0.5 : 1,
        },
      };
    }
    // existing schedule style
    const key = getStatusKey(event.resource?.status || "");
    const bg = STATUS_CONFIG[key]?.calColor || "#ff9800";
    return { style: { backgroundColor: bg, border: "none", borderRadius: 4, color: "#fff", fontSize: "0.75rem" } };
  }, []);

  const handleSelectEvent = (event) => {
    if (event.resource?.isTask) return; // tasks are read-only on calendar
    handleEditSchedule(event.resource);
  };

  const handleSelectSlot = ({ start }) => {
    setSelectedSchedule(null);
    setSelectedDate(start);
    setScheduleModalOpen(true);
  };

  // List filter
  const filteredSchedules = useMemo(() => {
    if (statusFilter === "todos") return schedules;
    return schedules.filter((s) => getStatusKey(s.status) === statusFilter);
  }, [schedules, statusFilter]);

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("schedules.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        reload={fetchSchedules}
        scheduleId={selectedSchedule?.id}
        contactId={contactId}
        cleanContact={cleanContact}
        selectedDate={selectedDate}
      />

      <MainHeader>
        <Title>{i18n.t("schedules.title")}</Title>
        <MainHeaderButtonsWrapper>
          {viewMode === "list" && (
            <TextField
              placeholder="Buscar..."
              type="search"
              value={searchParam}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Tooltip title={showTasks ? "Ocultar tarefas" : "Mostrar tarefas no calendário"}>
            <IconButton
              size="small"
              onClick={() => setShowTasks((v) => !v)}
              color={showTasks ? "primary" : "default"}
              style={{ border: showTasks ? `1px solid currentColor` : `1px solid transparent` }}
            >
              <FormatListBulletedSharp fontSize="small" />
            </IconButton>
          </Tooltip>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="calendar">
              <Tooltip title="Calendário">
                <CalendarTodayIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="list">
              <Tooltip title="Lista">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenScheduleModal}
          >
            {i18n.t("schedules.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {viewMode === "calendar" ? (
        <Paper className={classes.calendarPaper} variant="outlined">
          <div className={classes.calendarWrapper}>
            <Calendar
              localizer={localizer}
              culture="pt-BR"
              messages={calendarMessages}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 580 }}
              selectable
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              eventPropGetter={eventStyleGetter}
              views={["month", "week", "day", "agenda"]}
              defaultView="month"
              popup
            />
          </div>
          <Box className={classes.legendRow}>
            <Typography variant="caption" color="textSecondary">
              Legenda:
            </Typography>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <Box key={key} display="flex" alignItems="center" style={{ gap: 6 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    backgroundColor: cfg.calColor,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" color="textSecondary">
                  {cfg.label}
                </Typography>
              </Box>
            ))}
            {showTasks && (
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: "#7c3aed", display: "inline-block", flexShrink: 0, border: "2px dashed rgba(0,0,0,0.2)" }} />
                <Typography variant="caption" color="textSecondary">Tarefa</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      ) : (
        <Paper
          className={classes.mainPaper}
          variant="outlined"
          onScroll={handleScroll}
        >
          <Box className={classes.filterRow}>
            <Typography variant="caption" color="textSecondary">
              Filtrar:
            </Typography>
            {[
              { key: "todos",    label: "Todos" },
              { key: "pendente", label: "Pendentes" },
              { key: "enviado",  label: "Enviados" },
              { key: "erro",     label: "Erro" },
            ].map(({ key, label }) => {
              const cfg = STATUS_CONFIG[key];
              const active = statusFilter === key;
              return (
                <Chip
                  key={key}
                  label={label}
                  size="small"
                  clickable
                  onClick={() => setStatusFilter(key)}
                  variant={active ? "outlined" : "default"}
                  style={
                    active && cfg
                      ? { backgroundColor: cfg.bg, color: cfg.color, fontWeight: 700, borderColor: cfg.color }
                      : active
                      ? { fontWeight: 700 }
                      : {}
                  }
                />
              );
            })}
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("schedules.table.contact")}</TableCell>
                <TableCell>{i18n.t("schedules.table.body")}</TableCell>
                <TableCell>{i18n.t("schedules.table.sendAt")}</TableCell>
                <TableCell>{i18n.t("schedules.table.status")}</TableCell>
                <TableCell align="center">{i18n.t("schedules.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSchedules.map((schedule) => {
                const statusKey = getStatusKey(schedule.status);
                const cfg = STATUS_CONFIG[statusKey];
                const isSent = !!schedule.sentAt;
                return (
                  <TableRow key={schedule.id}>
                    <TableCell>{schedule.contact?.name}</TableCell>
                    <TableCell>
                      <Tooltip title={schedule.body} placement="top">
                        <span style={{ cursor: "default" }}>
                          {schedule.body.length > 40
                            ? schedule.body.substring(0, 40) + "..."
                            : schedule.body}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {moment(schedule.sendAt).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cfg.label}
                        size="small"
                        style={{
                          backgroundColor: cfg.bg,
                          color: cfg.color,
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={isSent ? "Mensagem já enviada" : "Editar agendamento"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleEditSchedule(schedule)}
                            disabled={isSent}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Excluir agendamento">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingSchedule(schedule);
                          }}
                          style={{ color: theme.palette.error.main }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && <TableRowSkeleton columns={5} />}
            </TableBody>
          </Table>

          {!loading && filteredSchedules.length === 0 && (
            <Box className={classes.emptyState}>
              <EventBusyIcon style={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant="body1" style={{ opacity: 0.5 }}>
                {statusFilter === "todos"
                  ? "Nenhum agendamento encontrado"
                  : `Nenhum agendamento ${STATUS_CONFIG[statusFilter]?.label?.toLowerCase()}`}
              </Typography>
              {statusFilter !== "todos" && (
                <Button size="small" onClick={() => setStatusFilter("todos")}>
                  Limpar filtro
                </Button>
              )}
            </Box>
          )}
        </Paper>
      )}
    </MainContainer>
  );
};

export default Schedules;
